import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { generationsAPI, creditsAPI } from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [recentGenerations, setRecentGenerations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [genResponse, transResponse] = await Promise.all([
          generationsAPI.list(1, 6),
          creditsAPI.getTransactions(1, 5),
        ]);
        setRecentGenerations(genResponse.data.generations);
        setTransactions(transResponse.data.transactions);
        await refreshUser();
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshUser]);

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page dashboard">
      <div className="container">
        {/* Welcome Section */}
        <div className="dashboard-header">
          <div>
            <h1 className="page-title">Welcome back, {user?.name?.split(' ')[0]}!</h1>
            <p className="page-description">
              Ready to transform another space?
            </p>
          </div>
          <Link to="/generate" className="btn btn-primary btn-lg">
            New Design
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card card">
            <div className="stat-icon credits">C</div>
            <div className="stat-content">
              <span className="stat-value">{user?.credits?.toFixed(0) || 0}</span>
              <span className="stat-label">Credits Available</span>
            </div>
            <Link to="/pricing" className="stat-action">
              Buy More
            </Link>
          </div>
          <div className="stat-card card">
            <div className="stat-icon generations">G</div>
            <div className="stat-content">
              <span className="stat-value">{recentGenerations.length}</span>
              <span className="stat-label">Recent Designs</span>
            </div>
            <Link to="/gallery" className="stat-action">
              View All
            </Link>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid">
            <Link to="/generate?type=flooring" className="action-card card">
              <span className="action-icon">🏠</span>
              <span className="action-label">Flooring</span>
            </Link>
            <Link to="/generate?type=countertop" className="action-card card">
              <span className="action-icon">🍳</span>
              <span className="action-label">Countertops</span>
            </Link>
            <Link to="/generate?type=interior_paint" className="action-card card">
              <span className="action-icon">🎨</span>
              <span className="action-label">Interior Paint</span>
            </Link>
            <Link to="/generate?type=exterior_paint" className="action-card card">
              <span className="action-icon">🏡</span>
              <span className="action-label">Exterior Paint</span>
            </Link>
          </div>
        </div>

        {/* Recent Generations */}
        <div className="recent-section">
          <div className="section-header">
            <h2 className="section-title">Recent Designs</h2>
            <Link to="/gallery" className="section-link">
              View All →
            </Link>
          </div>
          {recentGenerations.length > 0 ? (
            <div className="generations-grid">
              {recentGenerations.map((gen) => (
                <div key={gen.id} className="generation-card card">
                  <div className="generation-image">
                    {gen.output_image_url ? (
                      <img src={gen.output_image_url} alt="Generated design" />
                    ) : (
                      <div className="generation-placeholder">
                        {gen.status === 'processing' ? (
                          <div className="spinner" />
                        ) : (
                          <span>Failed</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="generation-info">
                    <span className="generation-type">
                      {gen.generation_type.replace('_', ' ')}
                    </span>
                    <span className="generation-date">
                      {new Date(gen.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <span className="empty-state-icon">🎨</span>
              <h3 className="empty-state-title">No designs yet</h3>
              <p className="empty-state-text">
                Create your first AI-powered room design
              </p>
              <Link to="/generate" className="btn btn-primary">
                Start Designing
              </Link>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="transactions-section">
          <h2 className="section-title">Recent Activity</h2>
          {transactions.length > 0 ? (
            <div className="card">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>
                          <span className="transaction-type">
                            {tx.type.replace('_', ' ')}
                          </span>
                          {tx.description && (
                            <span className="transaction-desc">
                              {tx.description}
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`transaction-amount ${
                              tx.amount >= 0 ? 'positive' : 'negative'
                            }`}
                          >
                            {tx.amount >= 0 ? '+' : ''}
                            {tx.amount}
                          </span>
                        </td>
                        <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="no-transactions">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
